import { Button, Input, toast } from '@app/ui';
import { FadeIn, PageTransition } from '@app/ui/motion';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

export const Route = createFileRoute('/admin/users')({
  validateSearch: z.object({
    search: z.string().optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.admin.users.queryOptions({
        input: { search: deps.search, page: deps.page },
      }),
    );
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Users,
});

function Users() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const currentSearch = search.search || '';
  const [searchInput, setSearchInput] = useState(currentSearch);

  // Sync local input with URL changes (e.g. browser back/forward)
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  const { data } = useSuspenseQuery(
    api.admin.users.queryOptions({
      input: { search: search.search, page: search.page },
    }),
  );

  const { users, total, page, totalPages } = data;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== currentSearch) {
        navigate({ search: (prev) => ({ ...prev, search: searchInput || undefined, page: 1 }) });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, currentSearch, navigate]);

  return (
    <PageTransition>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Users ({total})</h1>
        <Input
          type="search"
          placeholder="Search users..."
          className="w-64"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>
      <p className="mt-2 text-muted-foreground">
        Manage users, view details, and impersonate accounts.
      </p>

      <FadeIn>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Sport</th>
                <th className="py-2 pr-4">School</th>
                <th className="py-2 pr-4">Deals</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Joined</th>
                <th className="py-2">Plan</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-muted/50">
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        {user.image ? (
                          <img src={user.image} alt="" className="w-6 h-6 rounded-full" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                            {user.name.charAt(0)}
                          </div>
                        )}
                        {user.name}
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">{user.email}</td>
                    <td className="py-2 pr-4">{user.sport || '--'}</td>
                    <td className="py-2 pr-4">{user.school || '--'}</td>
                    <td className="py-2 pr-4">{user.dealCount}</td>
                    <td className="py-2 pr-4">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2">
                      <CompPlanControl userId={user.id} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FadeIn>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-3">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}
    </PageTransition>
  );
}

/**
 * Comp a plan to a user without Stripe.
 *
 * Exists so a pilot customer can be granted real access before their
 * billing path ships — the agent tier's checkout lands in a later PR,
 * but the pilot agent needs the product now.
 *
 * Deliberately minimal: a select plus a confirm. The mutation only ever
 * manages rows it created (identified by a null Stripe subscription id),
 * so a real paid subscription can never be clobbered from here.
 */
function CompPlanControl({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [plan, setPlan] = useState('');

  const grantMutation = useMutation({
    ...api.admin.grantPlan.mutationOptions(),
    onSuccess: (res) => {
      toast.success(
        res.plan === 'free' ? 'Comped plan revoked' : `Comped ${res.plan} for 12 months`,
      );
      setPlan('');
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'admin',
      });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Grant failed'),
  });

  return (
    <div className="flex items-center gap-1">
      <select
        aria-label="Comp a plan"
        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
        value={plan}
        onChange={(e) => setPlan(e.target.value)}
        disabled={grantMutation.isPending}
      >
        <option value="">Comp…</option>
        <option value="agent-solo">Agent Solo</option>
        <option value="agent-team">Agent Team</option>
        <option value="pro">Pro</option>
        <option value="free">Revoke</option>
      </select>
      {plan && (
        <Button
          size="sm"
          className="h-8 text-xs"
          onClick={() => grantMutation.mutate({ userId, plan, months: 12 })}
          disabled={grantMutation.isPending}
        >
          {grantMutation.isPending ? '…' : 'Apply'}
        </Button>
      )}
    </div>
  );
}
